import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { NotPermitted, PageHeader } from '@/design';
import { useCan } from '@/domains/auth';
import { ddApi } from '../../api/dd-api';
import type { DdMeetingKind } from '../../api/dd-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import { participantsApi } from '../../api/participants-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { recordAreaOf, recordSurveyPath } from '../../components/participant-link';
import { meetingKindOf, meetingLabel } from '../../dd';
import { refusalMessage } from '../../refusal';
import { ddAnchor, InterviewSheet, type IDdRevealState } from '../person-hub/due-diligence';

/**
 * A DD meeting assigned to the viewer (SR-501), opened beside My tasks: an in-person round or a
 * follow-up call, logged here — **the record's own interview sheet, drawn on the page**
 * (`InterviewSheet`, `inline`), so filling the answers and *Complete the meeting* are the same
 * writes the record page makes. The meeting is read by its id, never from My tasks' page, so it
 * stays open once completed or cancelled (and so off the viewer's tasks), its sheet read-only then;
 * the sheet starts from the page's read, and every write re-reads the list beside it. The header is
 * the participant's card — the name, the kind, the cycle — with the record one press away, at its
 * Due diligence block.
 */
export function AdminMyMeetingTaskPage() {
  const { interviewId = '' } = useParams();
  return <MyMeetingTask key={interviewId} interviewId={interviewId} />;
}

function MyMeetingTask({ interviewId }: { interviewId: string }) {
  const can = useCan();
  const readable = can('internal_fields.read');
  const rereadList = useListReread();
  // The kind a switch on the sheet moved the meeting to, which the header names.
  const [kind, setKind] = React.useState<Nullable<DdMeetingKind>>(null);
  const { data, error } = useAdminData(
    async (signal) => {
      if (!readable) return null;
      try {
        const read = await ddApi.read(interviewId, signal);
        const interview = read.body;
        const [participant, schema] = await Promise.all([
          participantsApi.getParticipant(interview.participantId, signal),
          // The scores' words only: a schema not read leaves the score box off, never the page.
          internalFieldsApi.schema(interview.survey).catch(() => null)
        ]);
        return { read, participant, schema };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read this meeting'));
      }
    },
    [interviewId, readable]
  );

  if (!readable || data === null || data === undefined) {
    return (
      <>
        <PageHeader title="Meeting" crumb="My tasks" />
        <div className="px-6 py-6">
          {!readable ? (
            <NotPermitted what="reading due diligence" />
          ) : error ? (
            <LoadError message={error} />
          ) : (
            <p className="text-sm text-muted-foreground">Reading the meeting…</p>
          )}
        </div>
      </>
    );
  }

  const { read, participant, schema } = data;
  const interview = read.body;
  const writable = can('internal_fields.write');
  const name = participant?.displayName ?? 'Unknown participant';
  const recordState: IDdRevealState = { revealDd: ddAnchor(interview.participantId, interview.survey) };
  const recordLink = recordSurveyPath(
    interview.participantId,
    interview.survey,
    recordAreaOf({ recordType: participant?.kind })
  );
  return (
    <>
      <PageHeader
        title={name}
        crumb="My tasks"
        lede={[
          meetingLabel(kind ?? meetingKindOf(interview)),
          interview.survey,
          `cycle ${interview.cycle}`,
          participant?.profile.firmName
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <Button asChild size="sm" variant="outline">
            <Link to={recordLink} state={recordState} data-task-source="">
              Open the record
            </Link>
          </Button>
        }
      />
      <div className="grid gap-4 px-6 py-6">
        {!writable && <NotPermitted what="recording due diligence" />}
        <InterviewSheet
          inline
          interviewId={interview.interviewId}
          kindHint={interview.kind}
          initial={read}
          schema={schema}
          name={name}
          writable={writable}
          openItems={null}
          onChanged={(next) => {
            // The header names the kind, which a switch moves; the list beside loses a meeting done.
            setKind(meetingKindOf(next));
            rereadList();
          }}
        />
      </div>
    </>
  );
}
